import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import './CustomerMaster.scss';
import './PersonnelPage.scss';
import './TimesheetsPage.scss';

const Field = ({ label, wide, required, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>
      {label}
      {required ? <span className="text-danger"> *</span> : null}
    </Form.Label>
    {children}
  </Form.Group>
);

const dateInput = (value) => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const EMPTY = {
  workType: 'tender',
  tenderId: '',
  assignmentId: '',
  workDate: new Date().toISOString().slice(0, 10),
  activityId: '',
  hours: '',
  description: '',
  correctionNote: '',
  filter: ''
};

const TimesheetFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { timesheetId } = useParams();
  const user = useSelector((state) => state.auth.user);
  const myId = user?._id || user?.id;
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = location.pathname.endsWith('/edit');
  const requestId = useRef(typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `ts-${Date.now()}`);
  const [form, setForm] = useState(EMPTY);
  const [record, setRecord] = useState(null);
  const [meta, setMeta] = useState(null);
  const [approver, setApprover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [returnComments, setReturnComments] = useState('');
  const [reopenReason, setReopenReason] = useState('');

  const own = record ? String(record.userId) === String(myId) : true;
  const editable = isCreate || (isEdit && own && ['draft', 'returned'].includes(record?.status));
  const heading = isCreate ? 'New timesheet' : (record?.personName || 'Timesheet');
  useDocumentTitle(heading);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const metaResponse = await platformAPI.timesheetMeta();
        if (cancelled) return;
        const nextMeta = metaResponse.data?.data || {};
        setMeta(nextMeta);
        if (!isCreate) {
          const response = await platformAPI.getTimesheet(timesheetId);
          const row = response.data?.data?.timesheet;
          if (!cancelled && row) {
            setRecord(row);
            setForm({
              workType: row.workType || (row.assignmentId ? 'assignment' : 'tender'),
              tenderId: row.tenderId || '',
              assignmentId: row.assignmentId || '',
              workDate: dateInput(row.workDate),
              activityId: row.activityId || '',
              hours: row.hours ?? '',
              description: row.description || '',
              correctionNote: '',
              filter: ''
            });
            setApprover({ approverName: row.approverName || '' });
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to open this timesheet');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, timesheetId]);

  const activities = useMemo(() => {
    const rows = meta?.activities || [];
    return rows.filter((item) => {
      const fits = item.workType === 'both' || item.workType === form.workType;
      const current = String(item._id) === String(form.activityId);
      return fits && (item.isActive || current);
    });
  }, [meta, form.workType, form.activityId]);

  const options = useMemo(() => {
    const query = form.filter.trim().toLowerCase();
    const source = form.workType === 'assignment' ? (meta?.assignments || []) : (meta?.tenders || []);
    return source.filter((item) => {
      const label = form.workType === 'assignment'
        ? `${item.reference || ''} ${item.name || ''} ${item.tenderReference || ''} ${item.clientName || ''}`
        : `${item.reference || ''} ${item.title || ''} ${item.organization || ''}`;
      return !query || label.toLowerCase().includes(query);
    });
  }, [meta, form.workType, form.filter]);

  useEffect(() => {
    if (!editable) return undefined;
    const selected = form.workType === 'assignment' ? form.assignmentId : form.tenderId;
    if (!selected) {
      setApprover(null);
      return undefined;
    }
    let cancelled = false;
    platformAPI.previewTimesheetApprover({
      workType: form.workType,
      tenderId: form.tenderId || undefined,
      assignmentId: form.assignmentId || undefined
    }).then((response) => {
      if (!cancelled) setApprover(response.data?.data || null);
    }).catch(() => {
      if (!cancelled) setApprover(null);
    });
    return () => { cancelled = true; };
  }, [editable, form.workType, form.tenderId, form.assignmentId]);

  const setField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'workType') {
        next.tenderId = '';
        next.assignmentId = '';
        next.activityId = '';
      }
      if (field === 'assignmentId') {
        const assignment = (meta?.assignments || []).find((item) => String(item._id) === String(value));
        next.tenderId = assignment?.tenderId || '';
      }
      return next;
    });
  };

  const payload = () => ({
    workType: form.workType,
    tenderId: form.workType === 'tender' ? form.tenderId : undefined,
    assignmentId: form.workType === 'assignment' ? form.assignmentId : undefined,
    workDate: form.workDate,
    activityId: form.activityId,
    hours: Number(form.hours),
    description: form.description,
    correctionNote: form.correctionNote || undefined,
    clientRequestId: requestId.current
  });

  const validate = () => {
    if (form.workType === 'tender' && !form.tenderId) return 'Choose a tender.';
    if (form.workType === 'assignment' && !form.assignmentId) return 'Choose an assignment.';
    if (!form.workDate) return 'Enter the work date.';
    if (!form.activityId) return 'Choose an activity.';
    if (!form.hours || Number(form.hours) <= 0) return 'Enter hours greater than zero.';
    return '';
  };

  const save = async (event) => {
    event?.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    if (saving) return null;
    setSaving(true);
    setError('');
    try {
      const response = isCreate
        ? await platformAPI.createTimesheet(payload())
        : await platformAPI.updateTimesheet(timesheetId, payload());
      const row = response.data?.data?.timesheet;
      return row;
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save this timesheet');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async (event) => {
    const row = await save(event);
    if (row?._id) navigate(`/apps/tender/timesheets/${row._id}`);
  };

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      let id = timesheetId;
      if (editable) {
        const problem = validate();
        if (problem) {
          setError(problem);
          setSaving(false);
          return;
        }
        const response = isCreate
          ? await platformAPI.createTimesheet(payload())
          : await platformAPI.updateTimesheet(timesheetId, payload());
        id = response.data?.data?.timesheet?._id || timesheetId;
      }
      await platformAPI.submitTimesheet(id, {});
      navigate('/apps/tender/timesheets');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit this timesheet');
      setSaving(false);
    }
  };

  const approve = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await platformAPI.approveTimesheet(timesheetId, {});
      navigate('/apps/tender/approvals');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not approve this timesheet');
      setSaving(false);
    }
  };

  const sendBack = async () => {
    if (!returnComments.trim()) {
      setError('A return comment is required.');
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await platformAPI.returnTimesheet(timesheetId, { comments: returnComments.trim() });
      navigate('/apps/tender/approvals');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not return this timesheet');
      setSaving(false);
    }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) {
      setError('A reason is required to reopen an approved entry.');
      return;
    }
    if (!window.confirm('Reopen this approved entry? It must be submitted and approved again.')) return;
    setSaving(true);
    try {
      await platformAPI.reopenTimesheet(timesheetId, { reason: reopenReason.trim() });
      navigate(`/apps/tender/timesheets/${timesheetId}/edit`);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not reopen this timesheet');
      setSaving(false);
    }
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  const locked = !editable;
  const status = record?.status || 'draft';
  const employeeName = record?.personName || meta?.employee?.name || user?.name || 'Signed-in user';
  const selectedAssignment = (meta?.assignments || []).find((item) => String(item._id) === String(form.assignmentId));

  return (
    <div className="cust-page">
      <form className={`cust-record pers-record${locked ? ' cust-record--view' : ''}`} onSubmit={saveDraft}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/timesheets')}>
              <BiArrowBack />
              Timesheets
            </button>
            <div className="cust-mast__row">
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">Timesheet</p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  <span className={`ts-page__badge ts-page__badge--${status}`}>{status}</span>
                  {record?.tenderReference ? <span className="cust-chip">{record.tenderReference}</span> : null}
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {isCreate || editable ? (
              <>
                <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate('/apps/tender/timesheets')} disabled={saving}>Cancel</Button>
                <Button size="sm" variant="outline-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</Button>
                <Button size="sm" variant="primary" type="button" onClick={submit} disabled={saving}>Submit for approval</Button>
              </>
            ) : null}
            {!isCreate && own && ['draft', 'returned'].includes(status) && !isEdit ? (
              <>
                <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate(`/apps/tender/timesheets/${timesheetId}/edit`)}>Edit</Button>
                <Button size="sm" variant="primary" type="button" onClick={submit} disabled={saving}>Submit for approval</Button>
              </>
            ) : null}
          </div>
        </header>

        {error ? <Alert variant="danger">{error}</Alert> : null}
        {record?.returnComments ? <Alert variant="warning">Returned: {record.returnComments}</Alert> : null}
        {record?.reopenReason ? <Alert variant="info">Last reopen: {record.reopenReason}</Alert> : null}

        <section className="cust-section">
          <div className="cust-section__head">
            <span>01</span>
            <div>
              <h2>Entry</h2>
              <p>Book time against a tender or an awarded assignment. The employee is the signed-in user.</p>
            </div>
          </div>
          <div className="cust-fields">
            <Field label="Employee" wide="6">
              <Form.Control value={employeeName} readOnly />
            </Field>
            <Field label="Status" wide="6">
              <Form.Control value={status} readOnly />
            </Field>
            <Field label="Work against" wide="4" required>
              <Form.Select value={form.workType} onChange={setField('workType')} disabled={locked}>
                <option value="tender">Tender</option>
                <option value="assignment">Assignment</option>
              </Form.Select>
            </Field>
            <Field label="Find" wide="8">
              <Form.Control value={form.filter} onChange={setField('filter')} disabled={locked} placeholder="Search reference, name, or client" />
            </Field>
            {form.workType === 'tender' ? (
              <Field label="Tender" wide="12" required>
                <Form.Select value={form.tenderId} onChange={setField('tenderId')} disabled={locked} required>
                  <option value="">Select a tender</option>
                  {options.map((tender) => (
                    <option key={tender._id} value={tender._id}>{tender.reference} — {tender.title}</option>
                  ))}
                </Form.Select>
              </Field>
            ) : (
              <Field label="Assignment" wide="12" required>
                <Form.Select value={form.assignmentId} onChange={setField('assignmentId')} disabled={locked} required>
                  <option value="">Select an assignment</option>
                  {options.map((item) => (
                    <option key={item._id} value={item._id}>{item.reference || item.tenderReference} — {item.name || item.tenderTitle}</option>
                  ))}
                </Form.Select>
              </Field>
            )}
            {form.workType === 'assignment' && (selectedAssignment || approver) ? (
              <>
                <Field label="Linked tender" wide="6">
                  <Form.Control readOnly value={approver?.tenderReference ? `${approver.tenderReference} — ${approver.tenderTitle || ''}` : '—'} />
                </Field>
                <Field label="Client" wide="6">
                  <Form.Control readOnly value={selectedAssignment?.clientName || approver?.clientName || '—'} />
                </Field>
              </>
            ) : null}
            <Field label="Work date" wide="4" required>
              <Form.Control type="date" value={form.workDate} onChange={setField('workDate')} disabled={locked} required />
            </Field>
            <Field label="Activity" wide="8" required>
              <Form.Select value={form.activityId} onChange={setField('activityId')} disabled={locked} required>
                <option value="">Select an activity</option>
                {activities.map((item) => (
                  <option key={item._id} value={item._id}>{item.name}{item.isActive ? '' : ' (inactive)'}</option>
                ))}
              </Form.Select>
            </Field>
            <Field label="Hours" wide="4" required>
              <Form.Control
                type="number"
                min={meta?.settings?.hourIncrement || 0.5}
                step={meta?.settings?.hourIncrement || 0.5}
                max={meta?.settings?.dailyHoursLimit || 8}
                value={form.hours}
                onChange={setField('hours')}
                disabled={locked}
                required
              />
            </Field>
            <Field label="Assigned approver" wide="8">
              <Form.Control readOnly value={approver?.approverName || record?.approverName || 'Resolved when a tender or assignment is chosen'} />
            </Field>
            <Field label="Work description" wide="12">
              <Form.Control as="textarea" rows={3} value={form.description} onChange={setField('description')} disabled={locked} />
            </Field>
            {record?.rateMissing && meta?.canSeeCost ? (
              <Alert variant="warning">No applicable rate was found for this date. The effort cost is incomplete and is not treated as zero.</Alert>
            ) : null}
            {meta?.canSeeCost && record?.effortCostPaise != null ? (
              <Field label="Internal effort-cost estimate" wide="6">
                <Form.Control readOnly value={`₹ ${(record.effortCostPaise / 100).toLocaleString('en-IN')}`} />
              </Field>
            ) : null}
          </div>
        </section>

        {record?.history?.length ? (
          <section className="cust-section">
            <div className="cust-section__head"><span>02</span><div><h2>History</h2><p>Submission, return, approval, and reopen stay on the entry.</p></div></div>
            <ul className="mb-0">
              {record.history.map((item, index) => (
                <li key={`${item.at}-${index}`}>
                  {item.action} {item.fromStatus ? `${item.fromStatus} → ${item.toStatus}` : item.toStatus || ''} {item.note ? `· ${item.note}` : ''}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {!isCreate && meta?.canApprove && status === 'submitted' && !own ? (
          <section className="cust-section">
            <div className="cust-section__head"><span>03</span><div><h2>Review</h2><p>Approvers cannot change the hours. Return needs a comment.</p></div></div>
            <div className="d-flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="primary" onClick={approve} disabled={saving}>Approve</Button>
              <Form.Control style={{ maxWidth: '24rem' }} value={returnComments} onChange={(event) => setReturnComments(event.target.value)} placeholder="Comment required to return" />
              <Button type="button" size="sm" variant="outline-primary" onClick={sendBack} disabled={saving}>Return</Button>
            </div>
          </section>
        ) : null}

        {!isCreate && meta?.canReopen && status === 'approved' ? (
          <section className="cust-section">
            <div className="cust-section__head"><span>04</span><div><h2>Reopen</h2><p>Reopen keeps the previous approval and sends the entry back to draft.</p></div></div>
            <div className="d-flex flex-wrap gap-2">
              <Form.Control style={{ maxWidth: '24rem' }} value={reopenReason} onChange={(event) => setReopenReason(event.target.value)} placeholder="Reason required" />
              <Button type="button" size="sm" variant="outline-danger" onClick={reopen} disabled={saving}>Reopen</Button>
            </div>
          </section>
        ) : null}
      </form>
    </div>
  );
};

export default TimesheetFormPage;
